<script setup lang="ts">
import type { PropType } from 'vue'
import type { BlobObject } from '../../types/gallery'
import { encodeImageSlug, imageBaseName, imageUrl } from '../utils/url.ts'

type GalleryBreadcrumb = {
  label: string
  to?: string
}

const props = defineProps({
  galleryName: {
    type: String,
    required: true
  },
  galleryTitle: {
    type: String,
    required: false,
    default: ''
  },
  galleryDescription: {
    type: String,
    required: false,
    default: ''
  },
  breadcrumb: {
    type: Array as PropType<GalleryBreadcrumb[]>,
    required: false,
    default: () => []
  },
  galleryBasePath: {
    type: String,
    required: false,
    default: ''
  },
  galleryId: {
    type: String,
    required: false,
    default: ''
  },
  previewImagePath: {
    type: String,
    required: false,
    default: ''
  },
  youtubeEmbedUrls: {
    type: Array as PropType<string[]>,
    required: false,
    default: () => []
  },
  galleryEntryOrder: {
    type: Array as PropType<string[]>,
    required: false,
    default: () => []
  }
})

type OrderedGalleryEntry = {
  id: string
  type: 'image' | 'video'
  image?: BlobObject
  embedUrl?: string
}

const resolvedGalleryBasePath = computed(() => props.galleryBasePath || `/${props.galleryName}`)
const resolvedGalleryTitle = computed(() => props.galleryTitle || props.galleryName)
const resolvedGalleryDescription = computed(() => props.galleryDescription || '')
const currentPreviewImagePath = ref(props.previewImagePath)
const resolvedBreadcrumb = computed<GalleryBreadcrumb[]>(() => {
  if (props.breadcrumb.length) {
    return props.breadcrumb
  }

  return [{ label: resolvedGalleryTitle.value }]
})

const dropZoneRef = ref<HTMLElement>()
const fileInput = ref<HTMLInputElement>()
const deletingImg = ref('')
const deletingVideo = ref('')
const uploadingImg = ref(false)
const addingVideo = ref(false)
const reorderingEntry = ref(false)
const loadingImages = ref(false)
const editingMetaPathname = ref('')
const savingMeta = ref(false)
const youtubeLinkInput = ref('')
const videoEmbeds = ref<string[]>([...props.youtubeEmbedUrls])
const galleryEntryOrder = ref<string[]>([...props.galleryEntryOrder])
const editMetaTitle = ref('')
const editMetaDescription = ref('')
const editMetaCameraModel = ref('')
const editMetaAperture = ref('')
const editMetaFocalLength = ref('')
const editMetaExposureTime = ref('')

const toast = useToast()
const { uploadImages, deleteImage, images, getImages, updateImageMeta } = useFile()
const { loggedIn } = useUserSession()

const active = useState()
const { width } = useWindowSize()

const columnCount = computed(() => {
  if (width.value >= 1280) {
    return 3
  }

  if (width.value >= 768) {
    return 2
  }

  return 1
})

const uploadedImages = computed(() => (images.value || []).filter(image => !isUploadPlaceholder(image)))
const uploadPlaceholders = computed(() => (images.value || []).filter(image => isUploadPlaceholder(image)))
const availableEntryIds = computed(() => {
  const videoIds = videoEmbeds.value.map(embedUrl => `video:${embedUrl}`)
  const imageIds = uploadedImages.value.map(image => `image:${image.pathname}`)
  return [...videoIds, ...imageIds]
})
const pendingUploadSlotIds = computed(() => new Set(uploadPlaceholders.value
  .filter(image => !!image.uploadId)
  .map(image => `upload:${image.uploadId}`)))
const normalizedGalleryEntryOrder = computed(() => {
  const available = availableEntryIds.value
  const availableSet = new Set(available)
  const ordered: string[] = []

  for (const id of galleryEntryOrder.value) {
    if ((!availableSet.has(id) && !pendingUploadSlotIds.value.has(id)) || ordered.includes(id)) {
      continue
    }

    ordered.push(id)
  }

  for (const id of available) {
    if (!ordered.includes(id)) {
      ordered.push(id)
    }
  }

  return ordered
})
const orderedGalleryEntries = computed<OrderedGalleryEntry[]>(() => {
  const imageMap = new Map(uploadedImages.value.map(image => [`image:${image.pathname}`, image]))
  const videoMap = new Map(videoEmbeds.value.map(embedUrl => [`video:${embedUrl}`, embedUrl]))
  const entries: OrderedGalleryEntry[] = []

  for (const id of normalizedGalleryEntryOrder.value) {
    const image = imageMap.get(id)
    if (image) {
      entries.push({
        id,
        type: 'image',
        image
      })
      continue
    }

    const embedUrl = videoMap.get(id)
    if (embedUrl) {
      entries.push({
        id,
        type: 'video',
        embedUrl
      })
    }
  }

  return entries
})

const orderedVideoEntries = computed(() => orderedGalleryEntries.value
  .filter((entry): entry is OrderedGalleryEntry & { type: 'video', embedUrl: string } => entry.type === 'video' && !!entry.embedUrl))

const orderedImageEntries = computed(() => orderedGalleryEntries.value
  .filter((entry): entry is OrderedGalleryEntry & { type: 'image', image: BlobObject } => entry.type === 'image' && !!entry.image))

const distributedImageEntries = computed(() => {
  const source = orderedImageEntries.value
  const count = columnCount.value
  const columns = Array.from({ length: count }, () => [] as typeof source)

  source.forEach((entry, index) => {
    columns[index % count]!.push(entry)
  })

  return columns
})

const distributedUploadPlaceholders = computed(() => {
  const count = columnCount.value
  const columns = Array.from({ length: count }, () => [] as typeof uploadPlaceholders.value)

  uploadPlaceholders.value.forEach((placeholder, index) => {
    columns[(orderedImageEntries.value.length + index) % count]!.push(placeholder)
  })

  return columns
})

const hasVideoContent = computed(() => orderedVideoEntries.value.length > 0)
const hasImageEntries = computed(() => orderedImageEntries.value.length > 0)
const hasImageContent = computed(() => hasImageEntries.value || uploadPlaceholders.value.length > 0)
const showImageUpload = computed(() => loggedIn.value && !hasVideoContent.value)
const showVideoEmbed = computed(() => loggedIn.value && !hasImageContent.value)

const hasGalleryContent = computed(() => hasVideoContent.value || hasImageContent.value)

watch(() => props.previewImagePath, (value) => {
  currentPreviewImagePath.value = value
})

watch(() => props.youtubeEmbedUrls, (value) => {
  videoEmbeds.value = [...value]
}, { immediate: true })

watch(() => props.galleryEntryOrder, (value) => {
  galleryEntryOrder.value = [...value]
}, { immediate: true })

async function patchGallery(payload: Record<string, unknown>) {
  if (!props.galleryId) {
    return
  }

  await $fetch(`/api/galleries/${props.galleryId}`, {
    method: 'PATCH',
    body: {
      title: resolvedGalleryTitle.value,
      name: props.galleryName,
      description: props.galleryDescription,
      previewImagePath: currentPreviewImagePath.value || null,
      youtubeEmbedUrls: videoEmbeds.value,
      galleryEntryOrder: normalizedGalleryEntryOrder.value,
      ...payload
    }
  })
}

function extractYouTubeVideoId(urlInput: string): string | null {
  const value = urlInput.trim()
  if (!value) {
    return null
  }

  let parsed: URL
  try {
    parsed = new URL(value)
  }
  catch {
    return null
  }

  const hostname = parsed.hostname.replace(/^www\./, '')

  if (hostname === 'youtu.be') {
    const id = parsed.pathname.split('/').filter(Boolean)[0]
    return id && /^[A-Za-z0-9_-]{11}$/.test(id) ? id : null
  }

  if (hostname === 'youtube.com' || hostname === 'm.youtube.com') {
    if (parsed.pathname === '/watch') {
      const id = parsed.searchParams.get('v')
      return id && /^[A-Za-z0-9_-]{11}$/.test(id) ? id : null
    }

    if (parsed.pathname.startsWith('/shorts/') || parsed.pathname.startsWith('/embed/')) {
      const id = parsed.pathname.split('/').filter(Boolean)[1]
      return id && /^[A-Za-z0-9_-]{11}$/.test(id) ? id : null
    }
  }

  return null
}

function toYouTubeEmbedUrl(urlInput: string): string | null {
  const id = extractYouTubeVideoId(urlInput)
  return id ? `https://www.youtube.com/embed/${id}` : null
}

async function addYoutubeVideo() {
  if (!props.galleryId || addingVideo.value) {
    return
  }

  const embedUrl = toYouTubeEmbedUrl(youtubeLinkInput.value)

  if (!embedUrl) {
    toast.add({
      title: 'Invalid YouTube link',
      description: 'Please provide a valid YouTube URL.',
      color: 'red'
    })
    return
  }

  const nextUrls = Array.from(new Set([...videoEmbeds.value, embedUrl]))
  const videoEntryId = `video:${embedUrl}`
  const nextOrder = [...normalizedGalleryEntryOrder.value]
  if (!nextOrder.includes(videoEntryId)) {
    nextOrder.push(videoEntryId)
  }

  addingVideo.value = true

  await patchGallery({
    youtubeEmbedUrls: nextUrls,
    galleryEntryOrder: nextOrder
  }).then(() => {
    videoEmbeds.value = nextUrls
    galleryEntryOrder.value = nextOrder
    youtubeLinkInput.value = ''
  }).catch(() => {
    toast.add({ title: 'An error occured', description: 'Please try again', color: 'red' })
  }).finally(() => {
    addingVideo.value = false
  })
}

function isUploadPlaceholder(image: { isUploading?: boolean }) {
  return !!image.isUploading
}

function uploadProgressPercent(image: { uploadProgress?: number }) {
  return Math.max(0, Math.min(100, image.uploadProgress ?? 0))
}

function openEditMetaModal(image: BlobObject) {
  editingMetaPathname.value = image.pathname
  editMetaTitle.value = image.meta?.title || ''
  editMetaDescription.value = image.meta?.description || ''
  editMetaCameraModel.value = image.meta?.cameraModel || ''
  editMetaAperture.value = image.meta?.aperture || ''
  editMetaFocalLength.value = image.meta?.focalLength || ''
  editMetaExposureTime.value = image.meta?.exposureTime || ''
}

function closeEditMetaModal() {
  editingMetaPathname.value = ''
  editMetaTitle.value = ''
  editMetaDescription.value = ''
  editMetaCameraModel.value = ''
  editMetaAperture.value = ''
  editMetaFocalLength.value = ''
  editMetaExposureTime.value = ''
}

async function saveImageMetadata() {
  if (!editingMetaPathname.value || savingMeta.value) {
    return
  }

  savingMeta.value = true

  await updateImageMeta(editingMetaPathname.value, {
    title: editMetaTitle.value,
    description: editMetaDescription.value,
    cameraModel: editMetaCameraModel.value,
    aperture: editMetaAperture.value,
    focalLength: editMetaFocalLength.value,
    exposureTime: editMetaExposureTime.value
  }).then(() => {
    closeEditMetaModal()
  }).catch(() => {
    toast.add({
      title: 'Failed to update image metadata',
      description: 'Please try again',
      color: 'red'
    })
  }).finally(() => {
    savingMeta.value = false
  })
}

const { isOverDropZone } = useDropZone(dropZoneRef, onDrop)

function openFilePicker() {
  fileInput.value?.click()
}

async function fileSelection(event: Event) {
  const target = event.target as HTMLInputElement
  const files = target.files ? Array.from(target.files) : []

  if (files.length) {
    await uploadFiles(files)
  }

  target.value = ''
}

async function onDrop(files: File[] | null) {
  if (files?.length) {
    await uploadFiles(files)
  }
}

async function uploadFiles(files: File[]) {
  uploadingImg.value = true

  await uploadImages(files, props.galleryName, undefined, {
    onUploadSlotsReserved: (order) => {
      galleryEntryOrder.value = order
    },
    onImageUploaded: (uploadId, image) => {
      const slotId = `upload:${uploadId}`
      const imageId = `image:${image.pathname}`
      galleryEntryOrder.value = galleryEntryOrder.value.includes(slotId)
        ? galleryEntryOrder.value.map(id => id === slotId ? imageId : id)
        : [...galleryEntryOrder.value, imageId]
    }
  })
    .catch(() => toast.add({ title: 'An error occured', description: 'Please try again', color: 'red' }))
    .finally(() => uploadingImg.value = false)
}

async function deleteFile(pathname: string) {
  deletingImg.value = pathname

  await deleteImage(pathname, props.galleryName)
    .catch(() => toast.add({ title: 'An error occured', description: 'Please try again', color: 'red' }))
    .finally(() => deletingImg.value = '')
}

async function deleteVideo(embedUrl: string) {
  if (!props.galleryId || deletingVideo.value) {
    return
  }

  deletingVideo.value = embedUrl

  const videoEntryId = `video:${embedUrl}`
  const nextUrls = videoEmbeds.value.filter(url => url !== embedUrl)
  const nextOrder = normalizedGalleryEntryOrder.value.filter(id => id !== videoEntryId)

  await patchGallery({
    youtubeEmbedUrls: nextUrls,
    galleryEntryOrder: nextOrder
  }).then(() => {
    videoEmbeds.value = nextUrls
    galleryEntryOrder.value = nextOrder
  }).catch(() => {
    toast.add({ title: 'An error occured', description: 'Could not remove video.', color: 'red' })
  }).finally(() => {
    deletingVideo.value = ''
  })
}

async function setPreviewImage(pathname: string) {
  if (!props.galleryId) {
    return
  }

  await patchGallery({
    previewImagePath: pathname
  }).then(() => {
    currentPreviewImagePath.value = pathname
    navigateTo(useRoute().fullPath, { replace: true })
  }).catch(() => {
    toast.add({ title: 'An error occured', description: 'Please try again', color: 'red' })
  })
}

async function moveEntry(entryId: string, direction: 'up' | 'down' | 'top' | 'bottom') {
  if (!props.galleryId || reorderingEntry.value) {
    return
  }

  const currentOrder = [...normalizedGalleryEntryOrder.value]
  const index = currentOrder.indexOf(entryId)

  if (index < 0) {
    return
  }

  const targetIndex = direction === 'up'
    ? index - 1
    : direction === 'down'
      ? index + 1
      : direction === 'top'
        ? 0
        : currentOrder.length - 1

  if (targetIndex < 0 || targetIndex >= currentOrder.length || targetIndex === index) {
    return
  }

  const nextOrder = [...currentOrder]

  if (direction === 'up' || direction === 'down') {
    const temp = nextOrder[targetIndex]
    nextOrder[targetIndex] = nextOrder[index]!
    nextOrder[index] = temp!
  }
  else {
    const [item] = nextOrder.splice(index, 1)
    if (!item) {
      return
    }
    nextOrder.splice(targetIndex, 0, item)
  }

  reorderingEntry.value = true

  await patchGallery({
    galleryEntryOrder: nextOrder
  }).then(() => {
    galleryEntryOrder.value = nextOrder
  }).catch(() => {
    toast.add({ title: 'An error occured', description: 'Could not reorder item.', color: 'red' })
  }).finally(() => {
    reorderingEntry.value = false
  })
}

async function reverseAllEntries() {
  if (!props.galleryId || reorderingEntry.value) {
    return
  }

  const nextOrder = [...normalizedGalleryEntryOrder.value].reverse()

  if (!nextOrder.length) {
    return
  }

  reorderingEntry.value = true

  await patchGallery({
    galleryEntryOrder: nextOrder
  }).then(() => {
    galleryEntryOrder.value = nextOrder
  }).catch(() => {
    toast.add({ title: 'An error occured', description: 'Could not reverse ordering.', color: 'red' })
  }).finally(() => {
    reorderingEntry.value = false
  })
}

async function sortEntriesByName() {
  if (!props.galleryId || reorderingEntry.value) {
    return
  }

  const nextOrder = [...normalizedGalleryEntryOrder.value]
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))

  if (!nextOrder.length) {
    return
  }

  reorderingEntry.value = true

  await patchGallery({
    galleryEntryOrder: nextOrder
  }).then(() => {
    galleryEntryOrder.value = nextOrder
  }).catch(() => {
    toast.add({ title: 'An error occured', description: 'Could not sort by name.', color: 'red' })
  }).finally(() => {
    reorderingEntry.value = false
  })
}

function canMoveEntry(entryId: string, direction: 'up' | 'down' | 'top' | 'bottom') {
  const index = normalizedGalleryEntryOrder.value.indexOf(entryId)
  if (index < 0) {
    return false
  }

  if (direction === 'up') {
    return index > 0
  }

  if (direction === 'top') {
    return index > 0
  }

  if (direction === 'bottom') {
    return index < normalizedGalleryEntryOrder.value.length - 1
  }

  return index < normalizedGalleryEntryOrder.value.length - 1
}

watch(() => props.galleryName, async (galleryName) => {
  if (!galleryName) {
    return
  }

  loadingImages.value = true
  await getImages(galleryName).finally(() => {
    loadingImages.value = false
  })
}, { immediate: true })
</script>

<template>
  <div>
    <section
      v-if="images"
      ref="dropZoneRef"
      class="relative min-h-screen gap-[22px] p-4"
    >
      <div class="max-w-6xl mx-auto space-y-3 mb-8">
        <AppBreadcrumbs :items="resolvedBreadcrumb" />

        <div class="flex items-center gap-3">
          <h1 class="text-3xl font-semibold text-white">
            {{ resolvedGalleryTitle }}
          </h1>
        </div>
        <slot
          name="header-actions"
          :reverse-order="reverseAllEntries"
          :sort-by-name="sortEntriesByName"
          :reversing-order="reorderingEntry"
        />

        <p class="text-gray-300">
          {{ resolvedGalleryDescription }}
        </p>
      </div>

      <div class="w-full">
        <div
          v-if="hasVideoContent"
          class="space-y-4 mb-8"
        >
          <div
            v-for="entry in orderedVideoEntries"
            :key="entry.id"
            class="relative w-full group"
          >
            <div class="overflow-hidden rounded-md border border-white/10 bg-black/40">
              <div class="relative w-full pt-[56.25%]">
                <iframe
                  :src="entry.embedUrl"
                  class="absolute inset-0 h-full w-full"
                  title="YouTube video"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerpolicy="strict-origin-when-cross-origin"
                  allowfullscreen
                />
              </div>
            </div>

            <div
              v-if="loggedIn"
              class="absolute top-4 right-4 z-[9999] flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <UButton
                icon="i-heroicons-bars-arrow-up-20-solid"
                color="neutral"
                variant="soft"
                aria-label="Move video to top"
                :disabled="!canMoveEntry(entry.id, 'top') || reorderingEntry"
                @click="moveEntry(entry.id, 'top')"
              />
              <UButton
                icon="i-heroicons-arrow-up-20-solid"
                color="neutral"
                variant="soft"
                aria-label="Move video up"
                :disabled="!canMoveEntry(entry.id, 'up') || reorderingEntry"
                @click="moveEntry(entry.id, 'up')"
              />
              <UButton
                icon="i-heroicons-arrow-down-20-solid"
                color="neutral"
                variant="soft"
                aria-label="Move video down"
                :disabled="!canMoveEntry(entry.id, 'down') || reorderingEntry"
                @click="moveEntry(entry.id, 'down')"
              />
              <UButton
                icon="i-heroicons-bars-arrow-down-20-solid"
                color="neutral"
                variant="soft"
                aria-label="Move video to bottom"
                :disabled="!canMoveEntry(entry.id, 'bottom') || reorderingEntry"
                @click="moveEntry(entry.id, 'bottom')"
              />
              <UButton
                :loading="deletingVideo === entry.embedUrl"
                color="neutral"
                variant="soft"
                label="Delete"
                icon="i-heroicons-trash-20-solid"
                @click="deleteVideo(entry.embedUrl)"
              />
            </div>
          </div>
        </div>

        <div
          v-if="hasImageContent"
          class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
        >
          <div
            v-for="(columnEntries, columnIndex) in distributedImageEntries"
            :key="`column-${columnIndex}`"
            class="space-y-4"
          >
            <div
              v-for="entry in columnEntries"
              :key="entry.id"
              class="relative w-full group masonry-item"
            >
              <template v-if="entry.type === 'image' && entry.image">
                <div class="absolute top-4 right-4 z-[9999] flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <UButton
                    v-if="loggedIn"
                    icon="i-heroicons-bars-arrow-up-20-solid"
                    color="neutral"
                    variant="soft"
                    aria-label="Move image to top"
                    :disabled="!canMoveEntry(entry.id, 'top') || reorderingEntry"
                    @click="moveEntry(entry.id, 'top')"
                  />
                  <UButton
                    v-if="loggedIn"
                    icon="i-heroicons-arrow-up-20-solid"
                    color="neutral"
                    variant="soft"
                    aria-label="Move image up"
                    :disabled="!canMoveEntry(entry.id, 'up') || reorderingEntry"
                    @click="moveEntry(entry.id, 'up')"
                  />
                  <UButton
                    v-if="loggedIn"
                    icon="i-heroicons-arrow-down-20-solid"
                    color="neutral"
                    variant="soft"
                    aria-label="Move image down"
                    :disabled="!canMoveEntry(entry.id, 'down') || reorderingEntry"
                    @click="moveEntry(entry.id, 'down')"
                  />
                  <UButton
                    v-if="loggedIn"
                    icon="i-heroicons-bars-arrow-down-20-solid"
                    color="neutral"
                    variant="soft"
                    aria-label="Move image to bottom"
                    :disabled="!canMoveEntry(entry.id, 'bottom') || reorderingEntry"
                    @click="moveEntry(entry.id, 'bottom')"
                  />
                  <UButton
                    v-if="loggedIn"
                    :loading="deletingImg === entry.image.pathname"
                    color="neutral"
                    variant="soft"
                    label="Delete"
                    icon="i-heroicons-trash-20-solid"
                    @click="deleteFile(entry.image.pathname)"
                  />
                  <UButton
                    v-if="loggedIn"
                    color="neutral"
                    variant="soft"
                    label="Preview"
                    icon="i-heroicons-photo-20-solid"
                    :class="currentPreviewImagePath === entry.image.pathname ? 'text-primary-400' : ''"
                    @click="setPreviewImage(entry.image.pathname)"
                  />
                  <UButton
                    v-if="loggedIn"
                    color="neutral"
                    variant="soft"
                    label="Meta"
                    icon="i-heroicons-pencil-square-20-solid"
                    @click="openEditMetaModal(entry.image)"
                  />
                </div>

                <UButton
                  v-if="loggedIn && !isUploadPlaceholder(entry.image)"
                  :loading="deletingImg === entry.image.pathname"
                  color="neutral"
                  variant="soft"
                  label="Delete"
                  icon="i-heroicons-trash-20-solid"
                  class="hidden"
                  @click="deleteFile(entry.image.pathname)"
                />
                <UButton
                  v-if="loggedIn && !isUploadPlaceholder(entry.image)"
                  color="neutral"
                  variant="soft"
                  label="Preview"
                  icon="i-heroicons-photo-20-solid"
                  class="hidden"
                  :class="currentPreviewImagePath === entry.image.pathname ? 'text-primary-400' : ''"
                  @click="setPreviewImage(entry.image.pathname)"
                />
                <NuxtLink
                  v-if="!isUploadPlaceholder(entry.image)"
                  :to="`${resolvedGalleryBasePath}/detail/${encodeImageSlug(entry.image.pathname)}`"
                  @click="active = entry.image.pathname.split('/').pop()?.split('.')[0]"
                >
                  <div class="relative">
                    <img
                      v-if="entry.image"
                      width="527"
                      height="430"
                      :src="imageUrl(entry.image.pathname, 'thumb')"
                      :class="{ imageEl: imageBaseName(entry.image.pathname) === active }"
                      class="h-auto w-full rounded-md transition-all duration-200 border-image brightness-[.8] hover:brightness-100 will-change-[filter]"
                    >
                    <div
                      v-if="entry.image.meta && (entry.image.meta.title || entry.image.meta.description || entry.image.meta.cameraModel || entry.image.meta.aperture || entry.image.meta.focalLength || entry.image.meta.exposureTime)"
                      class="absolute inset-x-0 bottom-0 rounded-b-md bg-gradient-to-t from-black/85 via-black/60 to-transparent p-3 text-xs text-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <p
                        v-if="entry.image.meta.title"
                        class="font-medium text-white"
                      >
                        {{ entry.image.meta.title }}
                      </p>
                      <p
                        v-if="entry.image.meta.description"
                        class="line-clamp-2"
                      >
                        {{ entry.image.meta.description }}
                      </p>
                      <p class="mt-1 text-[11px] text-gray-300">
                        {{ [entry.image.meta.cameraModel, entry.image.meta.aperture, entry.image.meta.focalLength, entry.image.meta.exposureTime].filter(Boolean).join(' · ') }}
                      </p>
                    </div>
                  </div>
                </NuxtLink>
              </template>
            </div>

            <div
              v-for="image in distributedUploadPlaceholders[columnIndex]"
              :key="image.pathname"
              class="rounded-md border border-white/10 bg-black/40 p-4 min-h-[220px] flex flex-col justify-center gap-4"
            >
              <div class="space-y-1">
                <p class="text-sm text-white font-medium truncate">
                  {{ image.uploadFileName || 'Uploading image...' }}
                </p>
                <p class="text-xs text-gray-400">
                  {{ image.uploadStatus === 'error' ? 'Upload failed' : 'Uploading...' }}
                </p>
              </div>

              <div class="w-full h-2 rounded bg-white/10 overflow-hidden">
                <div
                  class="h-full bg-primary-500 transition-all duration-300"
                  :style="`width: ${uploadProgressPercent(image)}%`"
                />
              </div>

              <p class="text-xs text-gray-400">
                {{ uploadProgressPercent(image) }}%
              </p>

              <p
                v-if="image.uploadStatus === 'error' && image.uploadError"
                class="text-xs text-red-300"
              >
                {{ image.uploadError }}
              </p>
            </div>
          </div>
        </div>

        <div
          v-else-if="!loadingImages && !hasGalleryContent"
          class="text-gray-400 text-center py-16"
        >
          No media yet in this gallery.
        </div>
        <div
          v-if="loggedIn"
          class="py-16"
        >
          <input
            v-if="showImageUpload"
            ref="fileInput"
            class="hidden"
            type="file"
            accept="image/*"
            multiple
            @change="fileSelection"
          >
          <UploadButton
            v-if="showImageUpload"
            :uploading="uploadingImg"
            type="submit"
            class="mb-6"
            :is-over-drop-zone="isOverDropZone"
            @click="openFilePicker"
          />

          <form
            v-if="showVideoEmbed"
            class="max-w-3xl mx-auto flex flex-col sm:flex-row gap-3"
            @submit.prevent="addYoutubeVideo"
          >
            <UInput
              v-model="youtubeLinkInput"
              class="flex-1"
              placeholder="Paste YouTube link to embed"
            />
            <UButton
              type="submit"
              :loading="addingVideo"
              label="Add video"
              color="primary"
            />
          </form>
        </div>
      </div>
    </section>
    <UModal
      :open="!!editingMetaPathname"
      title="Edit Image Metadata"
      :close="{ color: 'gray', variant: 'outline', class: 'rounded-full' }"
      @update:open="(open: boolean) => { if (!open) closeEditMetaModal() }"
    >
      <template #body>
        <form
          class="flex flex-col gap-3"
          @submit.prevent="saveImageMetadata"
        >
          <UInput
            v-model="editMetaTitle"
            placeholder="Title / headline"
          />
          <UTextarea
            v-model="editMetaDescription"
            placeholder="Description"
            :rows="3"
          />
          <UInput
            v-model="editMetaCameraModel"
            placeholder="Camera model"
          />
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <UInput
              v-model="editMetaAperture"
              placeholder="Aperture (e.g. f/2.8)"
            />
            <UInput
              v-model="editMetaFocalLength"
              placeholder="Focal length (e.g. 35mm)"
            />
            <UInput
              v-model="editMetaExposureTime"
              placeholder="Exposure (e.g. 1/125)"
            />
          </div>
          <div class="flex justify-end gap-2">
            <UButton
              type="button"
              color="neutral"
              variant="soft"
              label="Cancel"
              @click="closeEditMetaModal"
            />
            <UButton
              type="submit"
              color="primary"
              label="Save"
              :loading="savingMeta"
            />
          </div>
        </form>
      </template>
    </UModal>
    <div
      v-if="!images"
      class="flex items-center space-x-4 z-10"
    >
      <USkeleton
        class="h-12 w-12 bg-white-500"
        :ui="{ rounded: 'rounded-full' }"
      />
      <div class="space-y-2">
        <USkeleton class="h-4 w-[250px] bg-white-500" />
        <USkeleton class="h-4 w-[200px] bg-white-500" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
@media (min-width: 768px) {
  .imageEl {
    view-transition-name: vtn-image;
  }

  .bottom-menu-description {
    view-transition-name: vtn-bottom-menu-description;
  }

  .bottom-menu-button {
    view-transition-name: vtn-bottom-menu-button;
  }

  .container-image {
    background-color: rgba(255, 255, 255, 0.1)
  }

  .container-image:hover {
    background-color: transparent;
  }

  .border-image {
    border-width: 1.15px;
    border-color: rgba(255, 255, 255, 0.1)
  }
}

@media screen and (min-width: 1024px) {
  .masonry-item {
    width: 100%;
  }
}
</style>
